import { Injectable } from "@angular/core";
import { combineLatest, interval, Observable } from "rxjs";
import { filter, map } from "rxjs/operators";
import { BestFacade, BestPageViewModel } from "./best.facade";
import { BestVipLevelsThreshold } from "@modules/new_account/features/my-best/domain/entities/best-vip-levels-threshold.model";
import { FiatWallet } from "@models";
import { toEuroByFiatId } from "@helpers/tradable-convert";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { FiatService } from "@main-services/currency";
import { formatTimeSpan } from "@helpers/utils";
import { BEST_REWARDS_MAX_PERCENT_PRECISION, GLOBAL_MIN_TRADE_AMOUNT_IN_EUR } from "@helpers/constants";

@Injectable()
export class BestRewardsWeeklyFacade {
	private timeDifference: number = 0;

	constructor(
		private readonly bestFacade: BestFacade,
		private readonly fiatWalletService: FiatWalletService,
		private readonly fiatService: FiatService
	) {}

	public getBestRewards$(): Observable<BestRewardsViewModel> {
		return combineLatest([this.bestFacade.getBestPageViewModel$(), this.fiatWalletService.getList()]).pipe(
			// ignore initial state emit
			filter(([bestPageViewModel]) => !!bestPageViewModel),
			map(([bestPageViewModel, fiatWallets]) => {
				const nextRewardAt = new Date(bestPageViewModel.next_weekly_reward_at?.date_iso8601).valueOf();
				const isBuyButtonDisabled = this.getShouldDisableBuyButton(bestPageViewModel, fiatWallets);

				// fields "all_trade_bonus_levels" and "all_weekly_vip_levels" are now empty for lvl 0 x.x
				if (
					bestPageViewModel.all_trade_bonus_levels.length === 0 &&
					bestPageViewModel.all_weekly_vip_levels.length === 0
				) {
					return {
						...bestPageViewModel,
						isBuyButtonDisabled,
						nextRewardAt,
						nextTradeLevelBonusIncrease: "",
						vipLevelsMinAmounts: [],
						vipBonuses: [],
						nextVipLevelWithBonus: null,
						nextVipLevelBonusIncrease: "",
						nextVipThreshold: 0,
						tradingThresholds: [],
						tradeLevelBonuses: [],
						tradeBonus: 0,
						amountToNextVipLevel: 0,
						nextTradeThreshold: 0,
						amountToNextTradeLevel: 0,

						minNextTradeCrypto: 0,
						minNextTradeStocks: 0,

						rewardMonth: ""
					};
				}

				const nextTradeLevelBonusIncrease = this.getNextTradeLevelBonusIncrease(bestPageViewModel);
				const vipLevelsMinAmounts = this.getVipLevelsMinAmounts(bestPageViewModel);
				const vipBonuses = this.getVipLevelBonuses(bestPageViewModel);
				const nextVipLevelWithBonus = this.getNextVipLevelWithBonus(bestPageViewModel);
				const nextVipLevelBonusIncrease = this.getNextVipLevelBonusIncrease(bestPageViewModel);
				const amountToNextVipLevel = this.getAmountToNextVipLevel(bestPageViewModel);
				const nextVipThreshold = this.getNextVipThreshold(bestPageViewModel);
				const tradingThresholds = this.getTradingLevelsThresholds(bestPageViewModel);
				const tradeLevelBonuses = this.getTradeLevels(bestPageViewModel);
				const tradeBonus = this.getTradingBonus(bestPageViewModel, tradingThresholds);
				const nextTradeThreshold = this.getNextTradeThreshold(bestPageViewModel, tradingThresholds);
				const amountToNextTradeLevel = this.getAmountToNextTradingBonus(bestPageViewModel, tradeBonus);

				// Show minimum amount to trade (1 euro) if next trade crypto and/or stocks is lower than 1 euro
				const minNextTradeCrypto =
					bestPageViewModel.next_trade_crypto < GLOBAL_MIN_TRADE_AMOUNT_IN_EUR
						? GLOBAL_MIN_TRADE_AMOUNT_IN_EUR
						: bestPageViewModel.next_trade_crypto;
				const minNextTradeStocks =
					bestPageViewModel.next_trade_stocks < GLOBAL_MIN_TRADE_AMOUNT_IN_EUR
						? GLOBAL_MIN_TRADE_AMOUNT_IN_EUR
						: bestPageViewModel.next_trade_stocks;

				// TODO: some of these can be merged, e.g. nextvipThreshold & nextVipLevelBonusIncrease

				return {
					...bestPageViewModel,
					nextTradeLevelBonusIncrease,
					vipLevelsMinAmounts,
					vipBonuses,
					nextVipLevelWithBonus,
					nextVipLevelBonusIncrease,
					nextVipThreshold,
					tradingThresholds,
					tradeLevelBonuses,
					tradeBonus,
					amountToNextVipLevel,
					nextTradeThreshold,
					amountToNextTradeLevel,
					isBuyButtonDisabled,
					minNextTradeCrypto,
					minNextTradeStocks,
					nextRewardAt,

					rewardMonth: "" // to satisfy model constraints
				};
			})
		);
	}

	public getTimer$(startTime: number): Observable<string> {
		this.timeDifference = Math.round((startTime - new Date().valueOf()) / 1000);

		return interval(1000).pipe(
			map(() => {
				this.timeDifference = Math.max(0, this.timeDifference - 1);
				if (this.timeDifference === 0) {
					setTimeout(() => {
						this.bestFacade.fetchBest();
					}, 1000);
				}
				return formatTimeSpan(this.timeDifference).reverse().join(" : ");
			})
		);
	}

	private getAmountToNextTradingBonus(bestPageViewModel: BestPageViewModel, tradeBonus: number): number {
		return tradeBonus - +bestPageViewModel.trade_volume;
	}

	private getNextVipThreshold(bestPageViewModel: BestPageViewModel): number {
		const nextWeeklyVipLevel = bestPageViewModel.nextWeeklyVipLevel;

		// This means we are lvl 1 or 2
		if (nextWeeklyVipLevel?.rate === 0) {
			return +this.getNextVipLevelWithBonus(bestPageViewModel).min_amount;
		}
		return +nextWeeklyVipLevel?.min_amount;
	}

	private getNextTradeThreshold(bestPageViewModel: BestPageViewModel, tradingThresholds: number[]): number {
		const tradevolume = +bestPageViewModel.trade_volume;
		const nextThresholds = [];

		tradingThresholds.forEach((threshold) => {
			if (threshold > tradevolume) {
				nextThresholds.push(threshold);
			}
		});
		return nextThresholds[0];
	}

	private getAmountToNextVipLevel(bestPageViewModel: BestPageViewModel): number {
		const { balance, next_vip_levels_threshold_trade } = bestPageViewModel;
		const nextWeeklyVipLevel = bestPageViewModel.nextWeeklyVipLevel;
		const bestVolume: number = +balance;
		let amountToNextVipLevel: number;

		if (nextWeeklyVipLevel?.rate > 0) {
			amountToNextVipLevel = +next_vip_levels_threshold_trade;
			return amountToNextVipLevel;
		}

		const nextVipLevelWithBonus = this.getNextVipLevelWithBonus(bestPageViewModel);
		if (!nextVipLevelWithBonus) {
			return 0;
		}

		const nextVipBonusAmount = nextVipLevelWithBonus.min_amount;
		amountToNextVipLevel = +nextVipBonusAmount - bestVolume;
		return amountToNextVipLevel;
	}

	private getTradingBonus(bestPageViewModel: BestPageViewModel, tradingThresholds: number[]): number {
		return tradingThresholds.find((t) => t > +bestPageViewModel.trade_volume);
	}

	private getTradingLevelsThresholds(bestPageViewModel: BestPageViewModel): number[] {
		const tradeLevels = bestPageViewModel.all_trade_bonus_levels;
		return tradeLevels.map((t) => +t.min_amount).filter((t) => t > 0);
	}

	private getTradeLevels(bestPageViewModel: BestPageViewModel): string[] {
		const tradeLevels = bestPageViewModel.all_trade_bonus_levels;
		return tradeLevels
			.map((t) => +t.rate)
			.filter((t) => t > 0)
			.map((t) => `+${t.toFixed(BEST_REWARDS_MAX_PERCENT_PRECISION)}`);
	}

	private getNextVipLevelWithBonus(bestPageViewModel: BestPageViewModel): BestVipLevelsThreshold {
		const nextWeeklyVipLevel = bestPageViewModel.nextWeeklyVipLevel;
		const next = nextWeeklyVipLevel?.rate ?? 0; // If user gets to the max level, next level then = 0

		if (next > 0) {
			return nextWeeklyVipLevel;
		}

		// Find first level which offers a bonus (currently lvl 3)
		return bestPageViewModel.all_weekly_vip_levels.find((element) => element.rate > 0);
	}

	private getNextVipLevelBonusIncrease(bestPageViewModel: BestPageViewModel): string {
		const currentWeeklyVipLevel = bestPageViewModel.currentWeeklyVipLevel;
		const nextWeeklyVipLevel = bestPageViewModel.nextWeeklyVipLevel;

		const current = currentWeeklyVipLevel?.rate ?? 0;
		const next = nextWeeklyVipLevel?.rate ?? 0; // If user gets to the max level, next level then = 0

		if (next > 0) {
			return (next - current).toFixed(BEST_REWARDS_MAX_PERCENT_PRECISION);
		}

		// Find first level which offers a bonus (currently lvl 3)
		const startingLevel = bestPageViewModel.all_weekly_vip_levels.find((element) => element.rate > 0);
		if (!startingLevel) {
			return "0";
		}

		return (startingLevel.rate - current).toFixed(BEST_REWARDS_MAX_PERCENT_PRECISION);
	}

	private getNextTradeLevelBonusIncrease(bestPageViewModel: BestPageViewModel): string {
		const current = bestPageViewModel.currentTradeLevel?.rate ?? 0;
		const next = bestPageViewModel.nextTradeLevel?.rate ?? 0; // If user gets to the max level, next level then = 0

		return (next - current).toFixed(BEST_REWARDS_MAX_PERCENT_PRECISION);
	}

	private getVipLevelsMinAmounts(bestPageViewModel: BestPageViewModel): number[] {
		return bestPageViewModel.all_weekly_vip_levels
			.map((t) => {
				if (t.rate > 0) {
					return +t.min_amount;
				}
			})
			.filter((t) => !!t);
	}

	private getVipLevelBonuses(bestPageViewModel: BestPageViewModel): string[] {
		return bestPageViewModel.all_weekly_vip_levels
			.map((bonus) => bonus.rate)
			.filter((t) => t > 0)
			.map((t) => {
				const val = (+t).toFixed(BEST_REWARDS_MAX_PERCENT_PRECISION);
				return `+${val}`;
			});
	}

	private getShouldDisableBuyButton(bestPageViewModel: BestPageViewModel, fiatWallets: FiatWallet[]): boolean {
		const { bestAsset } = bestPageViewModel;
		return bestAsset.isMaintenance() || !bestAsset.isBuyActive();
	}

	private hasWalletWithEnoughFundsToBuyBest(
		bestPageViewModel: BestPageViewModel,
		fiatWallets: FiatWallet[]
	): boolean {
		return fiatWallets
			.filter((w) => w.getBalance() > 0)
			.some(
				(w) =>
					toEuroByFiatId(w.getBalance(), this.fiatService.getItemById(w.getFiatId())) >=
					bestPageViewModel.bestAsset.getMinBuy()
			);
	}
}

export interface BestRewardsViewModel extends BestPageViewModel {
	rewardMonth: string;
	isBuyButtonDisabled: boolean;
	nextRewardAt: number;

	// VIP levels
	currentVipLevel: BestVipLevelsThreshold;
	nextVipLevel: BestVipLevelsThreshold;
	vipLevelsMinAmounts: number[];
	vipBonuses: string[];
	nextVipLevelWithBonus: BestVipLevelsThreshold;
	nextVipLevelBonusIncrease: string;
	amountToNextVipLevel: number;
	nextVipThreshold: number;

	// Trading levels
	tradingThresholds: number[];
	tradeLevelBonuses: string[];
	tradeBonus: number;
	nextTradeThreshold: number;
	amountToNextTradeLevel: number;
	nextTradeLevelBonusIncrease: string;
	minNextTradeCrypto: number;
	minNextTradeStocks: number;
}
